import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import NotesIcon from "@mui/icons-material/Notes";
import PersonIcon from "@mui/icons-material/Person";
import {
  Box,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import React, { useMemo } from "react";
import { AuditLog, AuditLogLink, AuditSnapshot, getAuditDisplayText } from "../../../api/auditApi";
import { AuditEntityDetailCard } from "../auditEntityRegistry";
import {
  getAssignmentAssetLink,
  isAssignmentAudit,
} from "../utils/auditDetailsUtils";
import AssignmentActionDescription from "./AssignmentActionDescription";
import AuditActionChip from "./AuditActionChip";

interface AuditLogDetailsContentProps {
  auditLog: AuditLog | null;
  open?: boolean;
  compact?: boolean;
  excludeEntityType?: string;
  excludeEntityId?: number;
  previousSnapshot?: AuditSnapshot | null;
}

type AuditMetadata = Record<string, unknown>;
type AuditFieldChange = {
  field: string;
  oldValue: unknown;
  newValue: unknown;
};

const formatDateTime = (value: string) => new Date(value).toLocaleString();

const readNumber = (metadata: AuditMetadata, key: string) => {
  const value = metadata[key];
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const parseMetadata = (metadata: string | null): AuditMetadata => {
  if (!metadata) {
    return {};
  }

  try {
    const parsed = JSON.parse(metadata);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const readSnapshot = (metadata: AuditMetadata): AuditSnapshot | null => {
  const snapshot = metadata.snapshot;
  return isRecord(snapshot) ? snapshot : null;
};

const normalizeValue = (value: unknown) => {
  if (value === undefined) {
    return null;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  return value;
};

const areValuesEqual = (left: unknown, right: unknown) =>
  JSON.stringify(normalizeValue(left)) === JSON.stringify(normalizeValue(right));

const getSnapshotChanges = (
  snapshot: AuditSnapshot | null,
  previousSnapshot?: AuditSnapshot | null
): AuditFieldChange[] => {
  if (!snapshot) {
    return [];
  }

  const fieldNames = new Set([
    ...Object.keys(previousSnapshot ?? {}),
    ...Object.keys(snapshot),
  ]);

  return Array.from(fieldNames)
    .sort((left, right) => left.localeCompare(right))
    .filter((field) => !areValuesEqual(previousSnapshot?.[field] ?? null, snapshot[field] ?? null))
    .map((field) => ({
      field,
      oldValue: previousSnapshot?.[field] ?? null,
      newValue: snapshot[field] ?? null,
    }));
};

const formatSnapshotValue = (value: unknown) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "number" || typeof value === "string") {
    return String(value);
  }

  return JSON.stringify(value);
};

const formatFieldLabel = (field: string) =>
  field
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (value) => value.toUpperCase());

type DisplayAuditLink = Pick<AuditLogLink, "entityType" | "entityId" | "role">;

const getLinkRolePriority = (role: string) => {
  if (role === "Subject") {
    return 0;
  }

  if (role === "Related") {
    return 2;
  }

  return 1;
};

const FieldRow: React.FC<{ label: string; value?: React.ReactNode; icon?: React.ReactNode }> = ({
  icon,
  label,
  value,
}) => (
  <Box sx={{ alignItems: "flex-start", display: "flex", gap: 1, py: 0.5 }}>
    {icon && (
      <Box sx={{ color: "text.secondary", display: "flex", flexShrink: 0, pt: "2px" }}>
        {icon}
      </Box>
    )}
    <Typography color="text.secondary" sx={{ minWidth: icon ? 104 : 120, fontSize: "0.8125rem" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "0.8125rem", wordBreak: "break-word" }}>
      {value || "-"}
    </Typography>
  </Box>
);

const buildLegacyLinks = (auditLog: AuditLog, metadata: AuditMetadata): DisplayAuditLink[] => {
  const links: DisplayAuditLink[] = [];
  const addLink = (entityType: string, entityId?: number, role = entityType) => {
    if (entityId) {
      links.push({ entityType, entityId, role });
    }
  };

  addLink("Vehicle", readNumber(metadata, "vehicleId") || (auditLog.entityType === "Vehicle" ? auditLog.entityId : undefined), "Vehicle");
  addLink("Trailer", readNumber(metadata, "trailerId") || (auditLog.entityType === "Trailer" ? auditLog.entityId : undefined), "Trailer");
  addLink("EmployeeDriverInfo", readNumber(metadata, "employeeDriverInfoId"), "Driver");
  addLink("Employee", readNumber(metadata, "dispatcherEmployeeId"), "Dispatcher");

  return links;
};

const getDisplayLinks = (
  auditLog: AuditLog,
  metadata: AuditMetadata,
  excludeEntityType?: string,
  excludeEntityId?: number
): DisplayAuditLink[] => {
  const sourceLinks = auditLog.links?.length
    ? auditLog.links
    : buildLegacyLinks(auditLog, metadata);

  const uniqueLinks = new Map<string, DisplayAuditLink>();

  sourceLinks.forEach((link) => {
    if (!link.entityId || (excludeEntityType === link.entityType && excludeEntityId === link.entityId)) {
      return;
    }

    const linkKey = `${link.entityType}:${link.entityId}`;
    const nextLink = {
      entityType: link.entityType,
      entityId: link.entityId,
      role: link.role,
    };
    const currentLink = uniqueLinks.get(linkKey);

    if (!currentLink || getLinkRolePriority(nextLink.role) < getLinkRolePriority(currentLink.role)) {
      uniqueLinks.set(linkKey, nextLink);
    }
  });

  return Array.from(uniqueLinks.values());
};

const AuditLogDetailsContent: React.FC<AuditLogDetailsContentProps> = ({
  auditLog,
  open = true,
  compact = false,
  excludeEntityType,
  excludeEntityId,
  previousSnapshot,
}) => {
  const metadata = useMemo(() => parseMetadata(auditLog?.metadata ?? null), [auditLog?.metadata]);
  const snapshot = useMemo(() => readSnapshot(metadata), [metadata]);
  const snapshotChanges = useMemo(
    () => getSnapshotChanges(snapshot, previousSnapshot),
    [previousSnapshot, snapshot]
  );
  const hasSnapshotHistoryContext = previousSnapshot !== undefined;
  const relatedLinks = useMemo(
    () =>
      auditLog
        ? getDisplayLinks(auditLog, metadata, excludeEntityType, excludeEntityId)
        : [],
    [auditLog, excludeEntityId, excludeEntityType, metadata]
  );
  const assignmentAssetLink = useMemo(
    () =>
      auditLog && isAssignmentAudit(auditLog)
        ? getAssignmentAssetLink(auditLog, excludeEntityType, excludeEntityId)
        : null,
    [auditLog, excludeEntityId, excludeEntityType]
  );
  const detailLinks = assignmentAssetLink ? [assignmentAssetLink] : relatedLinks;
  const isAssignmentEntry = auditLog ? isAssignmentAudit(auditLog) : false;

  if (!auditLog) {
    return <Typography color="text.secondary">Select an audit entry to view details.</Typography>;
  }

  const actor =
    auditLog.actorName ||
    auditLog.actorEmail ||
    (auditLog.actorUserId ? `User #${auditLog.actorUserId}` : "System");

  return (
    <Stack spacing={compact ? 2 : 3}>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.25 }}>
          Action Summary
        </Typography>
        <Box sx={{ mb: 1.25 }}>
          <AuditActionChip action={auditLog.action} size="medium" />
        </Box>
        {isAssignmentEntry && (
          <Box sx={{ mb: 1.25 }}>
            <AssignmentActionDescription auditLog={auditLog} open={open} />
          </Box>
        )}
        <FieldRow
          icon={<AccessTimeIcon fontSize="small" />}
          label="When"
          value={formatDateTime(auditLog.createdAt)}
        />
        <FieldRow icon={<PersonIcon fontSize="small" />} label="By" value={actor} />
        {!isAssignmentEntry && (
          <>
            <FieldRow
              icon={<FingerprintIcon fontSize="small" />}
              label="Entity"
              value={`${auditLog.entityType} #${auditLog.entityId}`}
            />
            <FieldRow icon={<NotesIcon fontSize="small" />} label="Details" value={getAuditDisplayText(auditLog)} />
          </>
        )}
      </Box>

      {detailLinks.length > 0 && (
        <>
          <Divider />
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: compact ? "1fr" : { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
            }}
          >
            {detailLinks.map((link) => (
              <AuditEntityDetailCard
                key={`${link.entityType}:${link.entityId}:${link.role}`}
                link={link}
                open={open}
                compact={compact}
              />
            ))}
          </Box>
        </>
      )}

      {!isAssignmentEntry && snapshot && (
        <>
          <Divider />
          {hasSnapshotHistoryContext && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Changes
              </Typography>
              {snapshotChanges.length > 0 ? (
                <Stack spacing={0.75}>
                  {snapshotChanges.map((change) => (
                    <Box
                      key={change.field}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, mb: 0.5 }}>
                        {formatFieldLabel(change.field)}
                      </Typography>
                      <Typography color="text.secondary" sx={{ fontSize: "0.8125rem", wordBreak: "break-word" }}>
                        {formatSnapshotValue(change.oldValue)} {" -> "} {formatSnapshotValue(change.newValue)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                  No audited field changes in the loaded history context.
                </Typography>
              )}
            </Box>
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              State at This Time
            </Typography>
            <Stack spacing={0.25}>
              {Object.entries(snapshot)
                .sort(([left], [right]) => left.localeCompare(right))
                .map(([field, value]) => (
                  <FieldRow
                    key={field}
                    label={formatFieldLabel(field)}
                    value={formatSnapshotValue(value)}
                  />
                ))}
            </Stack>
          </Box>
        </>
      )}
    </Stack>
  );
};

export default AuditLogDetailsContent;
